import type { HealthSourceId } from "./health";

export interface FinaleOption {
  id: string;
  label: string;
}

export interface FinaleVisual {
  kind:
    | "skeleton"
    | "muscle"
    | "animals"
    | "germs"
    | "worm"
    | "beacon"
    | "bar-chart";
  bars?: Array<{ label: string; value: number }>;
  caption: string;
}

export interface FinaleStep {
  id: string;
  prompt: string;
  options: FinaleOption[];
  correctOptionId: string;
  explanation: string;
  visual: FinaleVisual;
  sourceIds?: HealthSourceId[];
}

export interface FinaleEpisode {
  challengeId: string;
  story: string;
  steps: FinaleStep[];
}

export const finaleEpisodes: FinaleEpisode[] = [
  {
    challengeId: "finale.canopy-frame",
    story:
      "A wind-blown branch has blocked Fern's rabbit family from the safe canopy path. Build a strong rescue frame from skeleton knowledge.",
    steps: [
      {
        id: "frame-function",
        prompt:
          "Which plan uses the main jobs of a skeleton to make the rescue frame?",
        options: [
          {
            id: "strong",
            label:
              "Join firm pieces so the frame supports, gives shape, protects, and can move at its joints",
          },
          {
            id: "soft",
            label: "Use only loose cloth because bones have no supporting job",
          },
          {
            id: "move-alone",
            label: "Ask the hard frame to move by itself without muscles",
          },
        ],
        correctOptionId: "strong",
        explanation:
          "A skeleton is a strong frame of joined bones. It supports and shapes the body, protects organs, and gives muscles firm bones to pull for movement.",
        visual: {
          kind: "skeleton",
          caption: "Fern waits beside a jointed rabbit rescue-frame model.",
        },
      },
      {
        id: "protect-label",
        prompt:
          "The plan needs two protective signs. Which labels are accurate?",
        options: [
          { id: "skull-ribs", label: "Skull—brain; rib cage—heart and lungs" },
          { id: "jaw-heart", label: "Jaw—heart; hip—brain" },
          { id: "spine-lungs", label: "Spine—lungs; lower leg—brain" },
        ],
        correctOptionId: "skull-ribs",
        explanation:
          "The skull surrounds the brain. The rib cage surrounds the heart and lungs. The jaw, spine, hip, and upper/lower limb bones have different shapes and positions in the joined frame.",
        visual: {
          kind: "skeleton",
          caption:
            "The model highlights skull, jaw, spine, rib cage, hip, arms, and legs.",
        },
      },
      {
        id: "model-map",
        prompt:
          "Fern checks the final body map. Which note connects position, job, and model limits accurately?",
        options: [
          {
            id: "map",
            label:
              "The spine runs down the back to support the body; the hip joins the legs; the card model shows positions but cannot grow",
          },
          {
            id: "swap",
            label:
              "The hip protects the brain, while the spine belongs in the chest",
          },
          {
            id: "real",
            label: "The card model is exactly a growing living skeleton",
          },
        ],
        correctOptionId: "map",
        explanation:
          "The spine forms a supporting column down the back, and the hip area joins the legs to the body. A card model can show those positions but leaves out living tissues and cannot grow.",
        visual: {
          kind: "skeleton",
          caption: "The rescue body map highlights the spine and hip.",
        },
      },
    ],
  },
  {
    challengeId: "finale.motion-crossing",
    story:
      "Mara needs a gentle lift mechanism to raise a floating log and reopen the beavers' crossing.",
    steps: [
      {
        id: "paired-pull",
        prompt: "Which mechanism action bends the model arm to lift the log?",
        options: [
          {
            id: "pair",
            label:
              "Front muscle contracts, shortens, and thickens as it pulls the bone; its partner relaxes",
          },
          { id: "push", label: "Both muscles push the bone and grow longer" },
          { id: "bone-alone", label: "The bone moves itself without muscles" },
        ],
        correctOptionId: "pair",
        explanation:
          "Muscles pull bones in pairs. To bend the arm, the front muscle contracts, becoming shorter and thicker, while its partner relaxes, lengthens, and thins. Their jobs swap to straighten it.",
        visual: {
          kind: "muscle",
          caption: "A paired-muscle lift holds the floating log safely.",
        },
      },
      {
        id: "movement-chart",
        prompt:
          "The beavers observed three playful routes. Which route had the most movement time?",
        options: [
          { id: "river", label: "River play—18 minutes" },
          { id: "meadow", label: "Meadow walk—12 minutes" },
          { id: "rock", label: "Rock-hop path—9 minutes" },
        ],
        correctOptionId: "river",
        explanation:
          "The longest bar is River play at 18 minutes. A bar chart supports this comparison; it does not score anyone's body or prescribe a personal target.",
        visual: {
          kind: "bar-chart",
          caption: "Observed playful movement time in minutes.",
          bars: [
            { label: "Meadow", value: 12 },
            { label: "River", value: 18 },
            { label: "Rock-hop", value: 9 },
          ],
        },
      },
      {
        id: "movement-model",
        prompt: "What is a careful conclusion for the field journal?",
        options: [
          {
            id: "benefits",
            label:
              "Many enjoyable movements can support heart, lungs, muscles, bones, sleep, thinking, and mood; the model leaves out living tissues",
          },
          { id: "fastest", label: "Only the fastest activity counts" },
          {
            id: "perfect",
            label: "Everyone needs the same perfect movement plan",
          },
        ],
        correctOptionId: "benefits",
        explanation:
          "Different enjoyable movements can support health, and rest matters too. A card-and-elastic model shows paired pulling around a joint but leaves out living tissues.",
        visual: {
          kind: "muscle",
          caption:
            "Mara celebrates a calm crossing with several movement choices.",
        },
      },
    ],
  },
  {
    challengeId: "finale.mystery-habitats",
    story:
      "Cove's storm map has three unfamiliar visitors. Follow observable evidence so each can reach a suitable habitat path.",
    steps: [
      {
        id: "dragonfly-key",
        prompt:
          "A dragonfly has no backbone and a hard jointed outer covering. Where does the key lead?",
        options: [
          { id: "exo", label: "Invertebrate with an exoskeleton" },
          { id: "bird", label: "Bird because it has wings" },
          { id: "reptile", label: "Reptile because its outside looks firm" },
        ],
        correctOptionId: "exo",
        explanation:
          "The dragonfly is an invertebrate because it has no backbone. Its hard jointed outer covering is an exoskeleton that supports and protects it.",
        visual: {
          kind: "animals",
          caption:
            "The dragonfly evidence card shows no backbone and a hard jointed covering.",
        },
      },
      {
        id: "otter-group",
        prompt:
          "Mara the beaver has a backbone, fur, and breathes air. Which school-level vertebrate group fits the evidence?",
        options: [
          { id: "mammal", label: "Mammal" },
          { id: "fish", label: "Fish because Mara swims" },
          {
            id: "amphibian",
            label: "Amphibian because Mara visits shore and water",
          },
        ],
        correctOptionId: "mammal",
        explanation:
          "Mara the beaver is a mammal. Her fur and backbone are relevant evidence; swimming habitat alone does not make an animal a fish. The other vertebrate groups are fish, amphibians, reptiles, and birds.",
        visual: {
          kind: "animals",
          caption:
            "Mara the beaver's card discloses backbone, fur, and air breathing.",
        },
      },
      {
        id: "key-method",
        prompt: "How should you use the repaired identification key?",
        options: [
          {
            id: "branches",
            label:
              "Start at question 1 and follow one yes-or-no branch using disclosed traits",
          },
          { id: "colour", label: "Guess from colour without reading evidence" },
          { id: "home", label: "Use habitat alone for every animal" },
        ],
        correctOptionId: "branches",
        explanation:
          "A readable key begins at its first yes-or-no question and follows disclosed evidence. Traits such as fins and gills, smooth moist skin, dry scales, feathers, and fur support the simplified five-group key.",
        visual: {
          kind: "animals",
          caption: "Labelled yes and no paths reconnect the coast habitats.",
        },
      },
    ],
  },
  {
    challengeId: "finale.care-signal",
    story:
      "Sage's care signal must guide a sunflower, hedgehog, and habitat keeper without alarming anyone or giving personal medical advice.",
    steps: [
      {
        id: "infection",
        prompt:
          "Which message accurately explains the three gentle fictional germ trails?",
        options: [
          {
            id: "some",
            label:
              "Some germs can enter and affect plants, animals, or people and cause infectious disease; hosts and routes differ",
          },
          {
            id: "all",
            label:
              "Every germ is harmful and affects every living thing in the same way",
          },
          { id: "people", label: "Only people can have infectious disease" },
        ],
        correctOptionId: "some",
        explanation:
          "Some germs can cause infectious disease after entering and affecting a living thing. Plants, animals, and people can be affected, but each disease has particular hosts and spread routes; not every germ is harmful.",
        visual: {
          kind: "germs",
          caption: "Three calm route cards show a plant, animal, and person.",
        },
        sourceIds: [
          "cdc-infection-control",
          "cdc-one-health",
          "fao-plant-health",
        ],
      },
      {
        id: "medicine",
        prompt:
          "A closed medicine container is found near the signal. What is the complete safe path?",
        options: [
          {
            id: "adult",
            label:
              "Leave it alone; tell a responsible adult or health professional; follow supplied instructions for amount and timing; never share",
          },
          { id: "open", label: "Open it and choose what the habitat needs" },
          {
            id: "friend",
            label: "Share it if a friend reports similar symptoms",
          },
        ],
        correctOptionId: "adult",
        explanation:
          "Never handle, choose, guess, or share medicine. It must come from a responsible adult or health professional, with its supplied label or instructions followed for amount and timing. Ask that adult or professional when unsure.",
        visual: {
          kind: "germs",
          caption: "A safety sign keeps the closed container untouched.",
        },
        sourceIds: ["nhs-child-medicines", "gosh-medicine-safety"],
      },
      {
        id: "vaccination",
        prompt: "Which statement belongs on the trustworthy care signal?",
        options: [
          {
            id: "some",
            label:
              "Vaccination can prevent some diseases in people and animals under professional guidance",
          },
          { id: "all", label: "Vaccination prevents every disease" },
          {
            id: "treat",
            label: "Vaccination treats any illness already present",
          },
        ],
        correctOptionId: "some",
        explanation:
          "WHO and the World Organisation for Animal Health support the bounded claim: vaccination can prevent some diseases in people and animals. It does not prevent every disease or replace professional guidance.",
        visual: {
          kind: "beacon",
          caption:
            "WHO and animal-health source seals light the care signal—no needle imagery.",
        },
        sourceIds: ["who-vaccination", "woah-animal-vaccination"],
      },
    ],
  },
  {
    challengeId: "finale.worm-garden",
    story:
      "An earthworm tunnel can carry compost to the celebration garden if you connect the soil clues.",
    steps: [
      {
        id: "worm-group",
        prompt:
          "The earthworm has no backbone and no hard outer covering. How should it be grouped?",
        options: [
          { id: "soft-invert", label: "Invertebrate without an exoskeleton" },
          { id: "vertebrate", label: "Vertebrate with a hidden backbone" },
          { id: "exo", label: "Invertebrate with a hard exoskeleton" },
        ],
        correctOptionId: "soft-invert",
        explanation:
          "An earthworm is an invertebrate because it has no backbone. It has a soft body rather than a hard supporting exoskeleton.",
        visual: {
          kind: "worm",
          caption:
            "A friendly earthworm moves freely through the compost tunnel.",
        },
      },
      {
        id: "compost",
        prompt: "Why should the tunnel lead to the garden compost?",
        options: [
          {
            id: "plants",
            label:
              "Worm activity helps break down material, and compost supports plant growth",
          },
          {
            id: "medicine",
            label: "Compost is medicine for every plant disease",
          },
          { id: "bones", label: "Earthworms build bones for plants" },
        ],
        correctOptionId: "plants",
        explanation:
          "Earthworms help break down material in compost. Compost returns useful material to soil and supports plant growth; it is not medicine or a cure for plant disease.",
        visual: {
          kind: "worm",
          caption: "The tunnel opens beside healthy celebration flowers.",
        },
      },
    ],
  },
  {
    challengeId: "finale.beacon-vocabulary",
    story:
      "One final Knowledge Beacon needs precise Living Things words before the sanctuary celebration can begin.",
    steps: [
      {
        id: "body-words",
        prompt: "Which pair completes the motion message accurately?",
        options: [
          {
            id: "contract-relax",
            label: "Contract—shorten and thicken; relax—lengthen and thin",
          },
          { id: "swap", label: "Contract—turn into bone; relax—push the bone" },
          {
            id: "same",
            label: "Contract and relax always mean the same state",
          },
        ],
        correctOptionId: "contract-relax",
        explanation:
          "A contracting muscle becomes shorter and thicker while its relaxing partner becomes longer and thinner. The pair pulls bones to move a joint.",
        visual: {
          kind: "beacon",
          caption: "The motion words light two linked sides of the beacon.",
        },
      },
      {
        id: "classification-words",
        prompt: "Which vocabulary set is accurate?",
        options: [
          {
            id: "groups",
            label:
              "Vertebrate—has a backbone; invertebrate—no backbone; exoskeleton—hard outer support",
          },
          {
            id: "habitat",
            label: "Vertebrate—lives on land; invertebrate—lives in water",
          },
          { id: "colour", label: "Exoskeleton—a colour pattern" },
        ],
        correctOptionId: "groups",
        explanation:
          "Backbone evidence separates vertebrates from invertebrates. An exoskeleton is a hard outer covering that supports and protects some invertebrates.",
        visual: {
          kind: "beacon",
          caption:
            "Backbone and outer-covering symbols light the classification ring.",
        },
      },
      {
        id: "health-words",
        prompt: "Which final vocabulary message is accurate and safe?",
        options: [
          {
            id: "safe",
            label:
              "Some germs can cause infectious disease; vaccination can prevent some diseases; medicine needs trusted guidance",
          },
          {
            id: "all",
            label:
              "All germs cause disease and one medicine works for everything",
          },
          {
            id: "personal",
            label: "A child should diagnose a friend and choose treatment",
          },
        ],
        correctOptionId: "safe",
        explanation:
          "Some germs can cause infectious disease, vaccination can prevent some diseases, and medicine always requires responsible-adult or health-professional guidance and supplied instructions. The beacon is ready.",
        visual: {
          kind: "beacon",
          caption: "The final beacon glows with safe, precise science words.",
        },
        sourceIds: [
          "cdc-infection-control",
          "who-vaccination",
          "nhs-child-medicines",
        ],
      },
    ],
  },
];

export const chapterVocabulary = [
  ["skeleton", "a strong frame of joined bones"],
  ["contract", "to become shorter and thicker as a muscle pulls"],
  ["relax", "to become longer and thinner while a partner muscle contracts"],
  ["vertebrate", "an animal with a backbone"],
  ["invertebrate", "an animal without a backbone"],
  [
    "exoskeleton",
    "a hard outer covering supporting and protecting some invertebrates",
  ],
  [
    "germ",
    "a tiny living thing or agent, some kinds of which can cause disease",
  ],
  [
    "infectious disease",
    "illness caused when certain germs enter and affect a living thing",
  ],
  ["vaccination", "a way to prepare body defences and prevent some diseases"],
] as const;
